import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../contexts/ToastContext';
import { IconCircleCheck, IconAlertCircle, IconInfoCircle, IconX } from '@tabler/icons-react';

const ToastIcon = ({ type }: { type: string }) => {
  const iconClass = "w-5 h-5";
  
  switch (type) {
    case 'success':
      return <IconCircleCheck className={`${iconClass} text-green-500`} />;
    case 'error':
      return <IconAlertCircle className={`${iconClass} text-red-500`} />;
    case 'warning':
      return <IconAlertCircle className={`${iconClass} text-amber-500`} />;
    case 'info':
      return <IconInfoCircle className={`${iconClass} text-blue-500`} />;
    default:
      return <IconInfoCircle className={`${iconClass} text-gray-500`} />;
  }
};

const ToastColors = {
  success: {
    bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
    border: 'border-green-200',
    text: 'text-green-800',
  },
  error: {
    bg: 'bg-gradient-to-r from-red-50 to-rose-50',
    border: 'border-red-200',
    text: 'text-red-800',
  },
  warning: {
    bg: 'bg-gradient-to-r from-amber-50 to-orange-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
  },
  info: {
    bg: 'bg-gradient-to-r from-blue-50 to-cyan-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
  },
};

export default function ToastContainer() {
  const { toasts, removeToast } = useToast();

  return (
    <div className="fixed bottom-0 right-0 z-[9999] p-4 space-y-2 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, x: 100 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 35,
            }}
            className="pointer-events-auto"
          >
            <div
              className={`
                ${ToastColors[toast.type].bg}
                ${ToastColors[toast.type].border}
                ${ToastColors[toast.type].text}
                flex items-center gap-3 p-4 rounded-xl border shadow-lg backdrop-blur-sm
                min-w-[320px] max-w-[420px]
                hover:shadow-xl transition-shadow duration-200
              `}
            >
              {/* Icon */}
              <div className="flex-shrink-0">
                <ToastIcon type={toast.type} />
              </div>

              {/* Message */}
              <p className="flex-1 text-sm font-medium leading-tight">
                {toast.message}
              </p>

              {/* Close Button */}
              <button
                onClick={() => removeToast(toast.id)}
                className="flex-shrink-0 p-1 rounded-lg hover:bg-black/5 transition-colors duration-150"
                aria-label="Close"
              >
                <IconX className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

